import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import axios from '../../../../api/axios'

/**
 * 笔记本上传 Hook
 * 处理上传逻辑、确认生成等
 */
export const useNotebookUpload = ({
  sources,
  selectedCourseCode,
  exerciseTypes,
  contentStyle,
  generateExercises = true,
  generateInlineExamples = true,
  inlineExampleTypes = null,
  setMessages,
  setIsUploading,
  setUploadProgress,
  setNotebookData,
  setGenerationProgress,
  setActiveTab,
  addNotification,
}) => {
  const { t } = useTranslation()
  const [uploadResult, setUploadResult] = useState(null)
  const sectionProgressPollRef = useRef(null)

  useEffect(() => {
    return () => {
      if (sectionProgressPollRef.current) {
        clearInterval(sectionProgressPollRef.current)
        sectionProgressPollRef.current = null
      }
    }
  }, [])

  const handleConfirmGenerate = useCallback(async (notebookData) => {
    console.log('[useNotebookUpload] handleConfirmGenerate 被调用')
    console.log('[useNotebookUpload] notebookData:', notebookData)
    console.log('[useNotebookUpload] originalFilePath:', notebookData?.originalFilePath)

    if (!notebookData || !notebookData.outline || notebookData.outline.length === 0) {
      console.error('[useNotebookUpload] 没有可用的 outline')
      setMessages((prev) => [
        ...prev,
        { type: 'error', content: '没有可用的 outline，请先上传文件生成大纲' },
      ])
      return
    }

    // 检查 originalFilePath
    if (!notebookData.originalFilePath) {
      console.error('[useNotebookUpload] 缺少 originalFilePath，notebookData:', notebookData)
      setMessages((prev) => [
        ...prev,
        { type: 'error', content: '缺少原始文件路径，无法生成章节。请重新上传文件。' },
      ])
      return
    }

    console.log('[useNotebookUpload] 开始生成章节，调用 API...')
    setIsUploading(true)
    setMessages((prev) => [
      ...prev,
      { type: 'system', content: '正在生成章节内容，这可能需要几分钟时间，请耐心等待...' },
    ])

    let startedPolling = false
    try {
      // 构建outline对象（优先使用保存的完整outline，否则从数组转换）
      let outlineObj = null
      if (notebookData.fullOutline) {
        outlineObj = notebookData.fullOutline
      } else {
        outlineObj = {
          notebook_title: notebookData.description || '',
          notebook_description: notebookData.description || '',
          outlines: {},
          section_markers: {},
        }

        notebookData.outline.forEach((item) => {
          outlineObj.outlines[item.title] = item.description || ''
          if (item.marker) {
            outlineObj.section_markers[item.title] = item.marker
          }
        })
      }

      // 笔记中的例子（概念块内、独立例子）允许的题型：勾选的类型名数组
      const inlineExampleTypesArray = inlineExampleTypes && typeof inlineExampleTypes === 'object'
        ? Object.entries(inlineExampleTypes).filter(([, v]) => v).map(([k]) => k)
        : []
      // Phase1 多步流程：确认生成仅生成原文（不生成题目）；可选是否生成笔记自带例子及题型
      const requestData = {
        notebookId: notebookData.notebookId || null,
        outline: outlineObj,
        originalFilePath: notebookData.originalFilePath,
        generateExercises: false,
        exerciseTypes: [],
        contentStyle: contentStyle,
        courseCode: selectedCourseCode ? selectedCourseCode.toUpperCase() : null,
        generateInlineExamples: generateInlineExamples !== false,
        inlineExampleTypes: inlineExampleTypesArray,
      }
      console.log('[useNotebookUpload] 发送确认生成请求:', requestData)
      console.log('[useNotebookUpload] originalFilePath 值:', requestData.originalFilePath)
      console.log('[useNotebookUpload] exerciseTypes 值:', requestData.exerciseTypes)
      console.log('[useNotebookUpload] contentStyle 值:', requestData.contentStyle)

      const response = await axios.post('/notes/confirm-generate', requestData, {
        timeout: 60 * 1000, // 仅等建立连接；实际生成异步，靠轮询
      })

      console.log('[useNotebookUpload] 确认生成响应:', response.status, response.data)

      if (response.status === 202 && response.data.progressId) {
        startedPolling = true
        const { progressId, notebookId: resNotebookId } = response.data
        setGenerationProgress({
          stage: 'generating_sections',
          timestamp: Date.now() / 1000,
          data: { message: '正在生成章节内容...', logs: [] },
        })

        const stopPolling = () => {
          if (sectionProgressPollRef.current) {
            clearInterval(sectionProgressPollRef.current)
            sectionProgressPollRef.current = null
          }
        }

        const poll = async () => {
          try {
            const pr = await axios.get(`/notes/upload/progress/${progressId}`)
            const p = pr.data
            setGenerationProgress(p)

            if (p.stage === 'error') {
              stopPolling()
              setIsUploading(false)
              addNotification({
                type: 'error',
                message: p.data?.message || p.data?.error || '生成章节失败',
                duration: 5000,
              })
              setMessages((prev) => [...prev, { type: 'error', content: p.data?.message || p.data?.error || '生成章节失败' }])
              return
            }

            if ((p.stage === 'sections_completed' || p.stage === 'completed') && p.data?.notebookReady) {
              stopPolling()
              const nid = p.data.notebookId || resNotebookId
              if (nid) {
                const nr = await axios.get(`/notes/${nid}`)
                const raw = nr.data
                const outlineObj = raw.outline || {}
                const outlines = outlineObj.outlines || {}
                const sectionMarkers = outlineObj.section_markers || {}
                const sectionSourceContent = outlineObj.section_source_content || {}
                const outlineArray = Object.entries(outlines).map(([title, desc], i) => ({
                  title,
                  description: desc || '',
                  order: i + 1,
                  marker: sectionMarkers[title] || '',
                  source_content: sectionSourceContent[title] || '',
                }))
                const sectionsObj = raw.sections || {}
                const sectionsArray = outlineArray
                  .map((oi) => {
                    const sec = sectionsObj[oi.title]
                    return sec ? { ...sec, section_title: oi.title } : null
                  })
                  .filter(Boolean)
                setNotebookData({
                  description: outlineObj.notebook_description || outlineObj.notebook_title || '',
                  outline: outlineArray,
                  sections: sectionsArray,
                  notebookId: nid,
                  originalFilePath: notebookData?.originalFilePath ?? raw.original_file_path,
                  fullOutline: outlineObj,
                })
              }
              setGenerationProgress((prev) => ({
                ...prev,
                stage: 'sections_completed',
                data: { ...(prev?.data || {}), message: '章节已生成完成' },
              }))
              addNotification({
                type: 'success',
                message: t('uploadNotebook.notebookReady') || '笔记本已经写好了！请查看右侧预览。',
                duration: 5000,
              })
              setMessages((prev) => [...prev, { type: 'system', content: t('uploadNotebook.notebookReady') || '✓ 笔记本已经写好了！请查看右侧预览。' }])
              setIsUploading(false)
            }
          } catch (e) {
            if (e.response?.status === 404) return
            console.error('[useNotebookUpload] 轮询进度失败:', e)
          }
        }

        poll()
        sectionProgressPollRef.current = setInterval(poll, 2000)
        return
      }

      setMessages((prev) => [...prev, { type: 'system', content: t('uploadNotebook.notebookReady') || '✓ 笔记本已经写好了！请查看右侧预览。' }])
    } catch (error) {
      console.error('Confirm generate error:', error)
      setMessages((prev) => [
        ...prev,
        { type: 'error', content: error.response?.data?.error || '生成章节失败，请重试' },
      ])
    } finally {
      if (!startedPolling) setIsUploading(false)
    }
  }, [contentStyle, selectedCourseCode, generateInlineExamples, inlineExampleTypes, t, setMessages, setIsUploading, setNotebookData, setGenerationProgress, addNotification])

  const handleUploadNotebook = useCallback(async () => {
    // Check if there's a file source
    const fileSource = sources.find((source) => source.file)
    if (!fileSource) {
      setMessages((prev) => [
        ...prev,
        { type: 'error', content: t('uploadNotebook.noFileSelected') || '请先选择要上传的文件' },
      ])
      return
    }

    if (!selectedCourseCode) {
      setMessages((prev) => [
        ...prev,
        { type: 'error', content: t('uploadNotebook.noCourseSelected') || '请先选择课程代码' },
      ])
      return
    }

    // Phase1 多步流程：首步仅生成大纲，不校验题目类型
    setIsUploading(true)
    setUploadProgress(0)
    setMessages([])
    setUploadResult(null)
    setNotebookData(null)
    setGenerationProgress(null)

    // Add user message
    setMessages((prev) => [
      ...prev,
      {
        type: 'user',
        content: t('uploadNotebook.uploadingFile')?.replace('{filename}', fileSource.name) || `正在上传文件: ${fileSource.name}`,
      },
    ])

    // Add system message with more details
    const fileExtension = fileSource.name.split('.').pop().toLowerCase()
    if (fileExtension !== 'json') {
      // 对于非JSON文件，立即切换到预览标签页，让用户看到流式生成
      setActiveTab(0)

      setMessages((prev) => [
        ...prev,
        {
          type: 'system',
          content: t('uploadNotebook.convertingFile') || '正在转换文件，这可能需要几分钟时间，请耐心等待...',
        },
        {
          type: 'system',
          content: t('uploadNotebook.generatingOutline') || '📝 正在生成笔记大纲...',
        },
      ])
    } else {
      setMessages((prev) => [
        ...prev,
        {
          type: 'system',
          content: t('uploadNotebook.validatingFile') || '正在验证文件格式...',
        },
        {
          type: 'system',
          content: t('uploadNotebook.validatingJson') || '✓ 正在验证 JSON 格式和结构...',
        },
      ])
    }

    try {
      // Create FormData
      const formData = new FormData()
      formData.append('notebook', fileSource.file)
      formData.append('courseCode', selectedCourseCode.toUpperCase())

      // 是否生成练习题
      formData.append('generateExercises', generateExercises !== false ? 'true' : 'false')

      // Add exercise types（仅当生成练习题时）
      if (generateExercises !== false) {
        const enabledExerciseTypes = Object.entries(exerciseTypes)
          .filter(([_, enabled]) => enabled)
          .map(([type, _]) => type)
        if (enabledExerciseTypes.length > 0) {
          formData.append('exerciseTypes', JSON.stringify(enabledExerciseTypes))
        }
      }

      // Add content style
      formData.append('contentStyle', contentStyle)

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) return prev
          return prev + 5
        })
      }, 500)

      // 对于非JSON文件，先生成大纲
      if (fileExtension !== 'json') {
        // 调用生成大纲API
        // 注意：不要手动设置 Content-Type，让浏览器自动设置（包括 boundary）
        const outlineResponse = await axios.post('/notes/generate-outline', formData, {
          timeout: 10 * 60 * 1000, // 10 minutes timeout
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              setUploadProgress(Math.min(percentCompleted, 90))
            }
          },
        })

        clearInterval(progressInterval)
        setUploadProgress(100)

        // 后端同步返回：Python 完成后返回完整大纲数据
        const data = outlineResponse.data
        if (data.notebookId && data.outline) {
          const newNotebookData = {
            description: (data.fullOutline?.notebook_description || data.fullOutline?.notebook_title) || '',
            outline: data.outline || [],
            sections: [],
            notebookId: data.notebookId,
            originalFilePath: data.originalFilePath || null,
            fullOutline: data.fullOutline || null,
          }
          setNotebookData(newNotebookData)
          setGenerationProgress({
            stage: 'outline_completed',
            timestamp: Date.now() / 1000,
            data: { outline: data.fullOutline, message: '大纲生成完成' },
          })
          addNotification({
            type: 'success',
            message: '大纲生成完成！',
            duration: 3000,
          })
          setActiveTab(0)
          setMessages((prev) => [
            ...prev,
            {
              type: 'system',
              content: '✓ 大纲生成完成！请查看预览，确认后点击「确认生成原文」生成笔记正文（本阶段不生成题目）。',
              showConfirmButton: true,
            },
          ])
        }
        setIsUploading(false)
        setTimeout(() => setUploadProgress(0), 1000)
        return
      }

      // JSON文件，直接上传（保持原有逻辑）
      const response = await axios.post('/notes/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 10 * 60 * 1000, // 10 minutes timeout
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setUploadProgress(Math.min(percentCompleted, 90))
          }
        },
      })

      clearInterval(progressInterval)
      setUploadProgress(100)

      // Success
      setUploadResult({
        success: true,
        message: t('uploadNotebook.uploadSuccess')?.replace('{title}', response.data.notebook.title) || `笔记 "${response.data.notebook.title}" 上传成功！`,
        notebook: response.data.notebook,
      })

      setMessages((prev) => [
        ...prev,
        {
          type: 'system',
          content: t('uploadNotebook.uploadSuccess')?.replace('{title}', response.data.notebook.title) || `✓ 笔记 "${response.data.notebook.title}" 上传成功！`,
        },
      ])

      // Fetch notebook data to display
      if (response.data.notebook?.id) {
        try {
          const notebookResponse = await axios.get(`/notes/${response.data.notebook.id}`)
          const notebook = notebookResponse.data

          const outlineSections = notebook.outline?.sections || []
          const sectionsObj = notebook.sections || {}

          const sectionsArray = outlineSections.map((outlineItem, index) => {
            const sectionTitle = outlineItem.title || outlineItem.section_title
            const section = sectionsObj[sectionTitle] || sectionsObj[Object.keys(sectionsObj)[index]]
            return {
              id: outlineItem.section_id || `section-${index}`,
              ...section,
              section_title: sectionTitle,
            }
          })

          const transformedData = {
            description: notebook.outline?.description || notebook.outline?.notebook_title || '',
            outline: outlineSections,
            sections: sectionsArray,
          }

          setNotebookData(transformedData)
        } catch (error) {
          console.error('Failed to fetch notebook data:', error)
        }
      }
    } catch (error) {
      console.error('Upload error:', error)
      let errorMessage = t('uploadNotebook.uploadFailed') || '上传失败，请检查文件格式和网络连接'
      let friendlyDetails = ''

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = t('uploadNotebook.timeoutError') || '请求超时。笔记转换可能需要较长时间，请稍后重试或检查网络连接。'
        friendlyDetails = t('uploadNotebook.timeoutHint') || '💡 提示：大文件转换可能需要 5-10 分钟，请耐心等待或稍后重试'
      } else if (error.response?.data?.error) {
        const errorType = error.response.data.error

        if (errorType.includes('JSON 格式错误') || errorType.includes('Unexpected end')) {
          errorMessage = t('uploadNotebook.jsonFormatError') || '❌ JSON 格式错误'
          friendlyDetails = t('uploadNotebook.jsonFormatHint') || '💡 可能的原因：\n1. 文件不完整或已损坏\n2. JSON 格式不正确（缺少括号、逗号等）\n3. 文件编码问题\n\n请检查文件是否完整，或尝试重新生成 JSON 文件'
          if (error.response.data.details) {
            friendlyDetails += `\n\n详细错误：${error.response.data.details}`
          }
          if (error.response.data.savedFile) {
            friendlyDetails += `\n\n${error.response.data.savedFile}`
          }
        } else if (errorType.includes('文件为空') || errorType.includes('empty')) {
          errorMessage = t('uploadNotebook.emptyFileError') || '❌ 文件为空'
          friendlyDetails = t('uploadNotebook.emptyFileHint') || '💡 上传的文件大小为 0 字节。请检查：\n1. 本地文件是否完整\n2. 文件是否被正确选择\n3. 网络上传是否中断'
        } else if (errorType.includes('转换失败') || errorType.includes('Conversion failed')) {
          errorMessage = t('uploadNotebook.conversionError') || '❌ 文件转换失败'
          friendlyDetails = t('uploadNotebook.conversionHint') || '💡 笔记转换过程中出现错误。请检查：\n1. 文件格式是否正确\n2. 文件内容是否完整\n3. 网络连接是否稳定'
          if (error.response.data.details) {
            friendlyDetails += `\n\n详细错误：${error.response.data.details}`
          }
          if (error.response.data.savedFile) {
            friendlyDetails += `\n\n${error.response.data.savedFile}`
          }
        } else {
          errorMessage = errorType
          if (error.response.data.details) {
            friendlyDetails = error.response.data.details
          }
        }
      } else if (error.response?.data?.details) {
        errorMessage = error.response.data.details
      } else if (error.message) {
        errorMessage = error.message
      }

      setUploadResult({
        success: false,
        message: errorMessage,
      })

      setMessages((prev) => [
        ...prev,
        { type: 'error', content: errorMessage },
        ...(friendlyDetails ? [{ type: 'system', content: friendlyDetails }] : []),
      ])
    } finally {
      setIsUploading(false)
      setTimeout(() => setUploadProgress(0), 1000)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- generateInlineExamples 仅用于内部逻辑，不触发重创建
  }, [
    sources,
    selectedCourseCode,
    exerciseTypes,
    contentStyle,
    generateExercises,
    t,
    setMessages,
    setIsUploading,
    setUploadProgress,
    setNotebookData,
    setGenerationProgress,
    setActiveTab,
    addNotification,
  ])

  return {
    uploadResult,
    handleUploadNotebook,
    handleConfirmGenerate,
  }
}
